import React, { useState, useEffect } from 'react';
import {
  X,
  Edit3,
  Star,
  Share2,
  Copy,
  History,
  Trash2,
  Lock,
  Unlock,
  Folder,
  Tag,
  Paperclip,
  Download,
  Shield,
  Clock,
  Check,
  CheckSquare,
  FileText,
  KeyRound,
  AlertCircle,
  Link2,
} from 'lucide-react';
import { copyTextToClipboard } from '../../utils/clipboard';

export default function SecureNoteViewModal({
  open,
  note,
  onClose,
  onEdit,
  onToggleFavorite,
  onDuplicate,
  onMoveToCategory,
  onManageTags,
  onShare,
  onViewActivity,
  onMoveToTrash,
  onUpdateContent,
  hasEditPermission = true,
  hasDeletePermission = true,
  hasSharePermission = true,
}) {
  const [unlocked, setUnlocked] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCodeIndex, setCopiedCodeIndex] = useState(null);

  // Lock body scroll on mobile when modal is open
  useEffect(() => {
    if (open) {
      document.body.classList.add('mobile-modal-open');
    } else {
      document.body.classList.remove('mobile-modal-open');
    }
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  if (!open || !note) return null;

  const isProtected = Boolean(note.isLocked) && !unlocked;

  const handleUnlock = (e) => {
    e.preventDefault();
    if (!pinInput) {
      setPinError('Please enter your note lock PIN');
      return;
    }
    if (note.lockPin && pinInput !== note.lockPin) {
      setPinError('Incorrect lock PIN');
      return;
    }
    setUnlocked(true);
    setPinError('');
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/apps/notes?noteId=${note.id}`;
    copyTextToClipboard(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyCode = (codeText, idx) => {
    copyTextToClipboard(codeText);
    setCopiedCodeIndex(idx);
    setTimeout(() => setCopiedCodeIndex(null), 2000);
  };

  const handleChecklistToggle = (lineIndex) => {
    if (!hasEditPermission || !onUpdateContent) return;
    const lines = (note.content || '').split('\n');
    const targetLine = lines[lineIndex];
    if (targetLine.includes('- [ ] ')) {
      lines[lineIndex] = targetLine.replace('- [ ] ', '- [x] ');
    } else if (targetLine.includes('- [x] ')) {
      lines[lineIndex] = targetLine.replace('- [x] ', '- [ ] ');
    }
    onUpdateContent(lines.join('\n'));
  };

  // Render markdown content
  const renderContent = () => {
    const text = note.content || '';
    if (!text.trim()) {
      return <p className="note-content-empty">(This note is empty)</p>;
    }

    const lines = text.split('\n');
    let inCodeBlock = false;
    let codeBuffer = [];
    let codeBlockIdx = 0;

    return (
      <div className="note-view-rendered">
        {lines.map((line, idx) => {
          if (line.trim().startsWith('```')) {
            if (inCodeBlock) {
              inCodeBlock = false;
              const codeText = codeBuffer.join('\n');
              codeBuffer = [];
              const currIdx = codeBlockIdx++;
              return (
                <div key={idx} className="note-code-block-wrapper">
                  <div className="note-code-header">
                    <span>Code</span>
                    <button
                      type="button"
                      className="btn-link btn-xs"
                      onClick={() => handleCopyCode(codeText, currIdx)}
                    >
                      {copiedCodeIndex === currIdx ? <Check size={12} /> : <Copy size={12} />}
                      <span>{copiedCodeIndex === currIdx ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>
                  <pre className="note-code-block">
                    <code>{codeText}</code>
                  </pre>
                </div>
              );
            } else {
              inCodeBlock = true;
              return null;
            }
          }
          if (inCodeBlock) {
            codeBuffer.push(line);
            return null;
          }

          if (line.startsWith('# ')) {
            return <h1 key={idx} className="note-h1">{line.replace('# ', '')}</h1>;
          }
          if (line.startsWith('## ')) {
            return <h2 key={idx} className="note-h2">{line.replace('## ', '')}</h2>;
          }
          if (line.startsWith('### ')) {
            return <h3 key={idx} className="note-h3">{line.replace('### ', '')}</h3>;
          }
          if (line.trim() === '---' || line.trim() === '***') {
            return <hr key={idx} className="note-divider" />;
          }
          if (line.startsWith('> ')) {
            return (
              <blockquote key={idx} className="note-blockquote">
                {line.replace('> ', '')}
              </blockquote>
            );
          }
          if (line.trim().startsWith('- [ ] ') || line.trim().startsWith('- [x] ')) {
            const checked = line.trim().startsWith('- [x] ');
            const labelText = line.trim().replace(checked ? '- [x] ' : '- [ ] ', '');
            return (
              <div
                key={idx}
                className={`note-checklist-item ${checked ? 'completed' : ''}`}
                onClick={() => handleChecklistToggle(idx)}
                style={{ cursor: hasEditPermission ? 'pointer' : 'default' }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!hasEditPermission}
                  onChange={() => handleChecklistToggle(idx)}
                  className="note-checklist-checkbox"
                />
                <span className="note-checklist-label">{labelText}</span>
              </div>
            );
          }
          if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
            return (
              <li key={idx} className="note-bullet-item">
                {line.trim().substring(2)}
              </li>
            );
          }
          const numMatch = line.trim().match(/^(\d+)\.\s+(.*)/);
          if (numMatch) {
            return (
              <div key={idx} className="note-ordered-item">
                <span className="note-ordered-num">{numMatch[1]}.</span>
                <span>{numMatch[2]}</span>
              </div>
            );
          }
          if (!line.trim()) {
            return <div key={idx} className="note-paragraph-gap" />;
          }
          return (
            <p key={idx} className="note-paragraph">
              {formatInlineText(line)}
            </p>
          );
        })}
      </div>
    );
  };

  const formatInlineText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('`') && part.endsWith('`')) {
        return <code key={i} className="note-inline-code">{part.slice(1, -1)}</code>;
      }
      return part;
    });
  };

  const attachments = Array.isArray(note.attachments) ? note.attachments : [];
  const tags = Array.isArray(note.tags) ? note.tags : [];

  return (
    <div className="modal-overlay animate-fade-in" onClick={onClose}>
      <div
        className="modal-container glass-panel secure-note-view-modal animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={note.title || 'Secure Note'}
      >
        {/* Header */}
        <div className="modal-header secure-note-view-header">
          <div className="view-header-meta">
            <div className="view-header-title-row">
              <h2 className="modal-title">{note.title || 'Untitled Note'}</h2>
              {note.is_favorite && (
                <span className="note-favorite-star active" title="Favorited">
                  <Star size={18} fill="#f59e0b" color="#f59e0b" />
                </span>
              )}
              {note.isLocked && (
                <span className="badge badge-warning badge-xs">
                  <Lock size={11} />
                  <span>Locked</span>
                </span>
              )}
            </div>

            <div className="view-meta-pills">
              {note.category && (
                <span className="note-category-pill">
                  <Folder size={12} />
                  <span>{note.category}</span>
                </span>
              )}
              {tags.map((tg) => (
                <span key={tg} className="note-tag-pill">
                  #{tg}
                </span>
              ))}
              <span className="note-time-info">
                <Clock size={12} />
                <span>
                  Updated {note.updated_at ? new Date(note.updated_at).toLocaleDateString() : 'recently'}
                </span>
              </span>
              <span className="zero-knowledge-badge">
                <Shield size={11} />
                <span>Decrypted Locally</span>
              </span>
            </div>
          </div>

          <div className="view-header-actions">
            {hasEditPermission && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  onClose();
                  onEdit(note);
                }}
              >
                <Edit3 size={14} />
                <span>Edit</span>
              </button>
            )}
            <button type="button" className="btn-icon modal-close-btn" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Lock Screen if note is locked */}
        {isProtected ? (
          <div className="modal-body note-lock-screen">
            <div className="note-lock-card">
              <div className="lock-icon-circle">
                <Lock size={32} color="#ef4444" />
              </div>
              <h3>Encrypted & Locked</h3>
              <p>This note is protected with an additional PIN lock. Enter the PIN to decrypt and reveal the content.</p>

              {pinError && (
                <div className="alert-banner alert-error">
                  <AlertCircle size={14} />
                  <span>{pinError}</span>
                </div>
              )}

              <form onSubmit={handleUnlock} className="lock-pin-form">
                <input
                  type="password"
                  className="input-field lock-pin-input"
                  placeholder="Enter PIN"
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value)}
                  autoFocus
                  maxLength={16}
                />
                <button type="submit" className="btn btn-primary">
                  <Unlock size={14} />
                  <span>Unlock Note</span>
                </button>
              </form>
            </div>
          </div>
        ) : (
          /* Normal Note Content */
          <div className="modal-body secure-note-view-body">
            {/* Rendered content */}
            <div className="note-content-display">
              {renderContent()}
            </div>

            {/* Attachments Section */}
            {attachments.length > 0 && (
              <div className="note-attachments-section">
                <h4 className="attachments-section-title">
                  <Paperclip size={15} />
                  <span>Encrypted Attachments ({attachments.length})</span>
                </h4>
                <div className="attachments-grid">
                  {attachments.map((att) => {
                    const sizeKb = (att.size / 1024).toFixed(1);
                    return (
                      <div key={att.id} className="attachment-card">
                        <div className="attachment-icon-box">
                          <FileText size={18} color="#3b82f6" />
                        </div>
                        <div className="attachment-info">
                          <strong className="attachment-name text-truncate" title={att.name}>
                            {att.name}
                          </strong>
                          <span className="attachment-meta">
                            {sizeKb} KB · {att.type || 'File'}
                          </span>
                        </div>
                        {att.data && (
                          <a
                            href={att.data}
                            download={att.name}
                            className="btn btn-secondary btn-xs"
                            title="Download attachment"
                          >
                            <Download size={13} />
                            <span>Download</span>
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer toolbar with actions */}
        <div className="modal-footer secure-note-view-footer">
          <div className="view-footer-left">
            <button
              type="button"
              className={`btn btn-secondary btn-sm ${note.is_favorite ? 'btn-fav-active' : ''}`}
              onClick={() => onToggleFavorite(note)}
              title={note.is_favorite ? 'Remove from Favorites' : 'Add to Favorites'}
            >
              <Star size={14} fill={note.is_favorite ? '#f59e0b' : 'none'} color={note.is_favorite ? '#f59e0b' : 'currentColor'} />
              <span>{note.is_favorite ? 'Favorited' : 'Favorite'}</span>
            </button>

            {hasSharePermission && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => onShare(note)}
                title="Share Note"
              >
                <Share2 size={14} />
                <span>Share</span>
              </button>
            )}

            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleCopyLink}
              title="Copy Note Link"
            >
              {copiedLink ? <Check size={14} color="#10b981" /> : <Link2 size={14} />}
              <span className="btn-label-full">{copiedLink ? 'Link Copied' : 'Copy Link'}</span>
              <span className="btn-label-short">{copiedLink ? 'Copied' : 'Copy'}</span>
            </button>

            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => onViewActivity(note)}
              title="View History & Audit Trail"
            >
              <History size={14} />
              <span>Activity</span>
            </button>
          </div>

          <div className="view-footer-right">
            {hasDeletePermission && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => {
                  onClose();
                  onMoveToTrash(note);
                }}
                title="Move Note to Trash"
              >
                <Trash2 size={14} />
                <span className="btn-label-full">Move to Trash</span>
                <span className="btn-label-short">Trash</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
